import { type FC, type ReactNode, useEffect, useState } from 'react';

import CloseCircle from '@crehana/icons/fill/CloseCircle';
import { useUITheme } from '@crehana/layouts';
// biome-ignore lint/style/noRestrictedImports: legacy import, will be migrated
import { colors, Pagination } from '@crehana/ui';

import CardContainer from '@/views/AdminDashboard/components/CardContainer';
import type { CardContainerProps } from '@/views/AdminDashboard/components/CardContainer/types';
import Table from '@/views/AdminDashboard/components/Table';
import type {
  Col,
  TableProps,
} from '@/views/AdminDashboard/components/Table/types';

import type { TInvalidRecords } from '../../../Step2/hooks/useDataValidation/types';

interface IPaginatedCardTable<T>
  extends Omit<TableProps<T>, 'data'>,
    Pick<CardContainerProps, 'isFlat' | 'counter' | 'hideDivider'> {
  errors: T[];
  title?: string;
  description?: ReactNode | string;
  isSingleTable?: boolean;
}

const ERRORS_IN_PAGE = 5;
const { redMain } = colors;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const PaginatedCardTable: FC<IPaginatedCardTable<any>> = ({
  errors,
  title,
  description,
  customKey = 'email',
  cols,
  isSingleTable,
  counter,
  isFlat,
  hideDivider,
  ...restProps
}) => {
  const { isDark } = useUITheme();
  const totalPages = Math.ceil(errors.length / ERRORS_IN_PAGE);

  const [currentPage, setCurrentPage] = useState(1);

  // biome-ignore lint/correctness/useExhaustiveDependencies: dependencies are intentional
  useEffect(() => {
    setCurrentPage(1);
  }, [errors]);

  const onPageChange = (nextPage: number) => {
    setCurrentPage(nextPage);
  };

  const startIndex = (currentPage - 1) * ERRORS_IN_PAGE;
  const endIndex = startIndex + ERRORS_IN_PAGE;
  const currentRecords = errors.slice(startIndex, endIndex);

  return (
    <div className="tw-mb-2">
      <CardContainer
        key={currentPage}
        isDark={isDark}
        counter={(isSingleTable || counter) && errors.length}
        counterDirection="left"
        icon={isSingleTable && <CloseCircle color={redMain} size={24} />}
        title={title}
        description={description}
        isFlat={isFlat}
        hideDivider={hideDivider}
      >
        <Table<TInvalidRecords>
          customKey={customKey as keyof TInvalidRecords}
          compact
          compactHeader
          dark={isDark}
          data={currentRecords}
          cols={cols as Col<TInvalidRecords>[]}
          {...restProps}
        />
        {totalPages > 1 && (
          <div className="flex justify-end pt-24">
            <Pagination
              isDark={isDark}
              displayPosition="top"
              onPageChange={onPageChange}
              currentPage={currentPage}
              totalPages={totalPages}
            />
          </div>
        )}
      </CardContainer>
    </div>
  );
};

export default PaginatedCardTable;
